import { AgreementType } from '@varo/graphql-schema-types';
import { useRouter } from 'expo-router';
import {
  fetchAgreement,
  transferData,
} from 'features/v2a/receiver/helpers/Payments';
import {
  Agreement,
  AgreementsData,
  ChallengeData,
  Data,
  ErrorMsgType,
  TRANSFER_RESPONSE_STATUS,
  TransferError,
  V2ABankAccountForm,
} from 'features/v2a/receiver/helpers/V2A.helpers';
import { useTranslatedError } from 'features/v2a/receiver/hooks/useTranslatedError';
import { V2AReceiverContext } from 'features/v2a/receiver/providers/V2AReceiverContextProvider';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AuthContext, AuthContextProps } from 'shared/providers/auth';
import {
  EnvironmentContext,
  EnvironmentContextProps,
} from 'shared/providers/environment';
import { logBreadcrumb } from 'shared/utils/sentry/sentry';
import { filterDigits } from 'shared/utils/string/string.helpers';

interface PaymentDetailsReturn {
  submitForm: (values?: V2ABankAccountForm) => Promise<void>;
  submitting: boolean;
  challengeData?: ChallengeData;
  errorResponse?: ErrorMsgType;
  agreementData?: AgreementsData;
  isTransferring?: boolean;
}

const usePaymentDetails = (): PaymentDetailsReturn => {
  const { getErrorMessage } = useTranslatedError();
  const { getVaroSessionId } = useContext<AuthContextProps>(AuthContext);
  const { paymentDetails } = useContext(V2AReceiverContext);

  const { currentEnv } =
    useContext<EnvironmentContextProps>(EnvironmentContext);
  const [errorResponse, setErrorResponse] = useState<ErrorMsgType>();
  const [agreementData, setAgreementData] = useState<AgreementsData>();
  const [challengeData, setChallengeData] = useState<ChallengeData>();
  const [cardData, setCardData] = useState<Data>();

  const [submitting, setSubmitting] = useState(false);
  const [isTransferring, setIsTransferring] = useState(false);
  const router = useRouter();

  const fetchIds = useCallback((): string[] => {
    const agreementIds = [] as string[];

    agreementData?.agreements?.forEach((item: Agreement) => {
      agreementIds.push(item.agreementId);
    });

    return agreementIds;
  }, [agreementData]);

  const handleTransfer = useCallback(
    async (cardDetails: Data) => {
      try {
        if (paymentDetails?.token) {
          const varoSessionId = await getVaroSessionId();
          const response = await transferData(
            cardDetails,
            paymentDetails,
            currentEnv,
            varoSessionId,
          );

          if (!response) {
            setErrorResponse(getErrorMessage('TERMINAL_ERROR'));
            return;
          }

          switch (response.status) {
            case TRANSFER_RESPONSE_STATUS.COMPLETED:
              setChallengeData(undefined);
              setCardData(undefined);
              setIsTransferring(false);
              router.push('/payments-confirmation');
              break;

            case TRANSFER_RESPONSE_STATUS.CHALLENGE:
              setCardData(cardDetails);
              setChallengeData(response.challenge as ChallengeData);
              break;

            default:
              setErrorResponse(getErrorMessage('TERMINAL_ERROR'));
              break;
          }
        } else {
          setErrorResponse(getErrorMessage('TERMINAL_ERROR'));
        }
      } catch (error) {
        let errorCode = 'UNKNOWN_ERROR';

        if (error instanceof Error) {
          try {
            const parsedError = JSON.parse(error.message) as TransferError;
            errorCode = parsedError.errorCode || 'UNKNOWN_ERROR';
          } catch (parseError) {
            logBreadcrumb({
              level: 'error',
              message: `${parseError}`,
            });
          }
        }
        setErrorResponse(getErrorMessage(errorCode));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentEnv, getErrorMessage, router, paymentDetails],
  );

  const formatFormData = useCallback(
    (values: V2ABankAccountForm): Data => {
      const date = values.date;
      const dateParts = date?.split('/') ?? [];
      const month = parseInt(dateParts[0]);
      const year = parseInt(`${20}${dateParts[1]}`); // This will only work until the year 2100, BE wants full YYYY string

      return {
        card: {
          name: {
            firstName: values.firstName,
            lastName: values.lastName,
          },
          email: values.email,
          cardCredentials: {
            cardNumber: filterDigits(values.cardNumber),
            expirationDate: {
              year: year,
              month: month,
            },
            cvv: values.cvv,
          },
          zipCode: values.zipCode,
          address: {
            state: values.state,
            postalCode: values.zipCode,
          },
        },
        consent: {
          agreementIds: fetchIds(),
          implicitConsent: false,
        },
      };
    },
    [fetchIds],
  );

  const submitForm = useCallback(
    async (values?: V2ABankAccountForm) => {
      setErrorResponse(undefined);
      setSubmitting(true);
      setIsTransferring(true);
      if (!values) {
        setSubmitting(false);
        return;
      }

      const cardDetails = cardData ?? formatFormData(values);

      await handleTransfer(cardDetails);

      setSubmitting(false);
    },
    [cardData, formatFormData, handleTransfer],
  );

  const fetchAgreementData = useCallback(
    async (type: string) => {
      try {
        if (paymentDetails?.token) {
          setErrorResponse(undefined);
          const varoSessionId = await getVaroSessionId();
          const response = await fetchAgreement(
            paymentDetails.token,
            type,
            currentEnv,
            varoSessionId,
          );

          if (response) {
            const agreementData = response as AgreementsData;
            setAgreementData((prevData) => ({
              agreements: [
                ...(prevData?.agreements || []),
                ...agreementData.agreements,
              ],
            }));
          }
        }
      } catch (error: unknown) {
        setErrorResponse(getErrorMessage((error as TransferError).errorCode));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [paymentDetails?.token, currentEnv, getErrorMessage],
  );

  const hasFetchedUserAgreement = useRef(false);
  const hasFetchedPolicyAgreement = useRef(false);

  useEffect(() => {
    if (!hasFetchedUserAgreement.current) {
      void fetchAgreementData(AgreementType.VaroToAnyoneUserAgreement);
      hasFetchedUserAgreement.current = true;
    }
    if (
      !errorResponse &&
      agreementData?.agreements.length === 1 &&
      !hasFetchedPolicyAgreement.current
    ) {
      void fetchAgreementData(AgreementType.OnlineAndMobilePrivacyPolicy);
      hasFetchedPolicyAgreement.current = true;
    }
  }, [fetchAgreementData, agreementData, errorResponse]);

  useEffect(() => {
    if (!paymentDetails && !errorResponse) {
      setErrorResponse(getErrorMessage('BAD_TOKEN'));
    }
  }, [paymentDetails, errorResponse, getErrorMessage]);

  return {
    submitForm,
    submitting,
    challengeData,
    errorResponse,
    agreementData,
    isTransferring,
  };
};

export default usePaymentDetails;
